/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useContext, useMemo, type PropsWithChildren } from 'react';
import { useOrgId } from '@jira/router-resources__navigation-organization-id/src/useOrgId.tsx';
import ErrorBoundary from '@jira/platform__error-boundary/src/ErrorBoundary.tsx';
import killswitch from '@jira/platform__utils-feature-gates-killswitch/src/index.tsx';
import { useProject } from '@jira/navigation-apps__sidebar-common/src/controllers/project/index.tsx';
import { getAnalyticsWebClientPromise } from '@jira/platform__observability-product-analytics-web-client-async';
import {
	subproductToEdition,
	tenantToPostOfficeContext,
	transformJiraEnvToPostOfficeEnvironment,
	useSubproduct,
} from '@jira/post-office__post-office-provider-preload-placement/src/tenant-to-post-office-context.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useActivationId } from '@atlassian/jira-tenant-context-controller/src/components/activation-id/index.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useEnvironment } from '@atlassian/jira-tenant-context-controller/src/components/environment/index.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import { PostOfficeProvider as PostOfficeBaseProvider } from '@atlassian/post-office-context/post-office';
import { usePostOfficeRouteUpdate } from '@atlassian/post-office-context/listener';
import { useRouter } from '@atlassian/react-resource-router';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { StableSubscriptionAdapterProvider } from '@atlassian/post-office-use-subscription/adapter-provider';
import { createUseSubscribeAdapter } from '@atlassian/post-office-use-subscription/create-use-subscribe-adapter';
import { CrossFlowPostOfficeContext } from './cross-flow-post-office-provider.tsx';
import { getOnboardingParams } from './onboarding-params.tsx';

/**
 * Optional provider that wraps subscription providers only if feature gate is enabled.
 * Defined at module level to avoid "component during render" (subtree remount).
 */
const OptionalPostOfficeSubscriptionsProvider = ({ children }: PropsWithChildren) => {
	// https://switcheroo.atlassian.com/ui/gates/8299833f-7cf4-453b-bd5b-16f3bcf81ddb/key/post-office_graphql_subscriptions_enabled
	const subscriptionAdapter = useMemo(
		() => (fg('post-office_graphql_subscriptions_enabled') ? createUseSubscribeAdapter() : null),
		[],
	);
	if (subscriptionAdapter) {
		return (
			<StableSubscriptionAdapterProvider subscriptionAdapter={subscriptionAdapter}>
				{children}
			</StableSubscriptionAdapterProvider>
		);
	}

	return <>{children}</>;
};

const JiraPostOfficeProvider = ({ children }: { children: React.ReactNode }) => {
	const { data } = useProject();
	const isSiteAdmin = useIsSiteAdmin();
	const isAdmin = useIsAdmin();
	const cloudId = useCloudId();
	const activationId = useActivationId();
	const locale = useLocale();
	const environment = useEnvironment();
	const { data: orgId } = useOrgId();
	const editions = useAppEditions();
	const subproduct = useSubproduct(data?.type);
	const edition = subproductToEdition(subproduct, editions);
	const additionalContext = useContext(CrossFlowPostOfficeContext);

	const context = useMemo(() => {
		return {
			current: {
				...tenantToPostOfficeContext({
					activationId,
					cloudId,
					isAdmin,
					isSiteAdmin,
					locale,
				}),
				orgId,
				productEdition: edition || undefined,
				subproduct,
				...additionalContext,
				...(fg('jira-onboarding-hub-gate') ? getOnboardingParams() : {}),
			},
		};
	}, [
		cloudId,
		orgId,
		activationId,
		subproduct,
		isSiteAdmin,
		isAdmin,
		locale,
		edition,
		additionalContext,
	]);

	const env = useMemo(
		() => ({ env: transformJiraEnvToPostOfficeEnvironment(environment) }),
		[environment],
	);

	const analyticsClient = useMemo(
		() => getAnalyticsWebClientPromise().then((client) => client.getInstance()),
		[],
	);

	return (
		<PostOfficeBaseProvider
			context={context}
			environment={env}
			analyticsClientInstance={analyticsClient}
		>
			<OptionalPostOfficeSubscriptionsProvider>{children}</OptionalPostOfficeSubscriptionsProvider>
		</PostOfficeBaseProvider>
	);
};

export const PostOfficeProvider = ({
	children,
}: {
	children: React.ReactNode;
}): React.JSX.Element => {
	// Using the Feature Gate used for the placement app-switcher-discovery-section because PostOfficeProvider is always needed when the app-switcher-discovery-section is enabled
	const isPostOfficeEnabled = !killswitch('killswitch_post_office_in_jira');

	return isPostOfficeEnabled ? (
		<ErrorBoundary id="postOfficeJiraProvider" render={() => <>{children}</>}>
			<JiraPostOfficeProvider>{children}</JiraPostOfficeProvider>
		</ErrorBoundary>
	) : (
		<>{children}</>
	);
};

const ReactResourceRouterListener = ({ children }: { children: React.ReactNode }) => {
	const [route] = useRouter();

	usePostOfficeRouteUpdate([route.basePath, route.location.pathname]);

	return <>{children}</>;
};

export const PostOfficeRouterListener = ({
	children,
}: {
	children: React.ReactNode;
}): React.JSX.Element => {
	const isPostOfficeEnabled = !killswitch('killswitch_post_office_in_jira');

	return isPostOfficeEnabled && fg('post-office-screen-space-overlay-jfe') ? (
		<ReactResourceRouterListener>{children}</ReactResourceRouterListener>
	) : (
		<>{children}</>
	);
};
